import { useCallback, useEffect, useMemo, useState } from 'react';
import type { SelectChangeEvent } from '@mui/material/Select';
import gamesService from '../../../services/gamesService';
import authService from '../../../services/authService';
import useStoreActivatedPacks from '../../../hooks/useStoreActivatedPacks';
import { useGames } from '../../../hooks/useGames';
import { useLottoSales } from '../../../hooks/useLottoSales';
import { usePayouts } from '../../../hooks/usePayouts';
import type { StoreLocation } from '../../../types/services/auth';

export const useCaseSimulatorPageLogic = (storeId?: string) => {
  const selectedStoreId = storeId || '';
  const hasStoreId = selectedStoreId.length > 0;
  const [createPackLoading, setCreatePackLoading] = useState(false);
  const [locations, setLocations] = useState<StoreLocation[]>([]);
  const [locationsResolved, setLocationsResolved] = useState(false);
  const [selectedLocationId, setSelectedLocationId] = useState('');

  const {
    packsData,
    storeData,
    processedPacks,
    loading: packsLoading,
    error: packsError,
    setError: setPacksError,
    loadStoreAndPacks,
    addNewPack,
    removePack,
  } = useStoreActivatedPacks({
    storeId: selectedStoreId,
    selectedLocationId,
  });

  const {
    games,
    selectedGame,
    loading: gamesLoading,
    scannerConfig,
    loadGames,
    handleGameChange,
    getSelectedGameData,
  } = useGames();

  const {
    payoutDialogOpen,
    payoutAmount,
    setPayoutAmount,
    posAmount,
    setPosAmount,
    selectedPayoutType,
    selectedPayoutPack,
    lotteryPayoutLoading,
    scratcherPayoutLoading,
    handleLotteryPayout,
    handlePayoutSubmit,
    handlePayoutCancel,
  } = usePayouts({ storeId: selectedStoreId, storeData, setError: setPacksError });

  const {
    lottoSaleDialogOpen,
    lottoSaleAmount,
    setLottoSaleAmount,
    lottoSaleLoading,
    handleLottoSale,
    handleLottoSaleSubmit,
    handleLottoSaleCancel,
  } = useLottoSales({ storeId: selectedStoreId, storeData, setError: setPacksError });

  useEffect(() => {
    if (hasStoreId && locationsResolved) {
      loadStoreAndPacks();
    }
  }, [hasStoreId, loadStoreAndPacks, locationsResolved]);

  useEffect(() => {
    if (storeData?.state) {
      loadGames(storeData.state);
    }
  }, [loadGames, storeData?.state]);

  useEffect(() => {
    if (!selectedStoreId) {
      setLocations([]);
      setLocationsResolved(false);
      setSelectedLocationId('');
      return;
    }

    let cancelled = false;
    setLocationsResolved(false);

    const loadLocations = async () => {
      try {
        const storeLocations = await authService.getStoreLocations(selectedStoreId);
        if (cancelled) return;

        const activeLocations = storeLocations.filter((location) => location.active !== false);
        const nextLocations = activeLocations.length > 0 ? activeLocations : storeLocations;
        setLocations(nextLocations);

        setSelectedLocationId((currentSelectedLocationId) => {
          const matchingLocation = nextLocations.find((location) => location.id === currentSelectedLocationId);
          if (matchingLocation) {
            return currentSelectedLocationId;
          }

          return '';
        });
        setLocationsResolved(true);
      } catch (error) {
        console.error('Error loading store locations:', error);
        if (!cancelled) {
          setLocations([]);
          setSelectedLocationId('');
          setLocationsResolved(true);
        }
      }
    };

    void loadLocations();

    return () => {
      cancelled = true;
    };
  }, [selectedStoreId]);

  const handleCreatePack = useCallback(async () => {
    if (!packsData || !selectedStoreId) {
      return;
    }

    const selectedGameData = getSelectedGameData();
    if (!selectedGameData) {
      return;
    }

    setCreatePackLoading(true);
    setPacksError(null);

    try {
      const barcodeFormat = scannerConfig?.barcode_format;
      if (!barcodeFormat) {
        throw new Error('Missing barcode format from games scanner config');
      }
      const newPack = gamesService.convertGameToPack(selectedGameData, 'scratchers', {
        barcodeFormat,
        direction: packsData.direction,
        regionStartingTicketNumber: packsData.region_starting_ticket_number ?? scannerConfig?.region_starting_ticket_number ?? 0,
      });
      const actualStoreKey = storeData?.store_key || selectedStoreId;
      const response = await gamesService.activatePack({
        store_key: actualStoreKey,
        pack_id: newPack.pack_id,
        game_id: selectedGameData.game_id?.toString(),
        activated_on: newPack.date_activated,
        state: selectedGameData.state,
        pack_type: 'scratchers',
      });

      if (response.success) {
        addNewPack(newPack);
        return;
      }

      setPacksError(`Failed to activate pack: ${response.message}`);
    } catch (error) {
      console.error('Error creating pack:', error);
      setPacksError('Failed to create pack. Please try again.');
    } finally {
      setCreatePackLoading(false);
    }
  }, [addNewPack, getSelectedGameData, packsData?.direction, packsData?.region_starting_ticket_number, scannerConfig?.barcode_format, scannerConfig?.region_starting_ticket_number, selectedStoreId, setPacksError, storeData?.store_key]);

  const handlePackReturn = useCallback((packId: string) => {
    removePack(packId);
  }, [removePack]);

  const onLotteryPayout = useCallback((type: 'lotto' | 'scratchers') => {
    handleLotteryPayout(type, processedPacks);
  }, [handleLotteryPayout, processedPacks]);

  const dismissError = useCallback(() => {
    setPacksError(null);
  }, [setPacksError]);

  const handleLocationChange = useCallback((event: SelectChangeEvent<string>) => {
    setSelectedLocationId(event.target.value);
  }, []);

  const storeName = useMemo(() => storeData?.name ?? 'Store', [storeData?.name]);

  const dialogs = useMemo(() => ({
    lottoSaleAmount,
    lottoSaleDialogOpen,
    payoutAmount,
    payoutDialogOpen,
    posAmount,
    selectedPayoutPack,
    selectedPayoutType,
    setLottoSaleAmount,
    setPayoutAmount,
    setPosAmount,
  }), [
    lottoSaleAmount,
    lottoSaleDialogOpen,
    payoutAmount,
    payoutDialogOpen,
    posAmount,
    selectedPayoutPack,
    selectedPayoutType,
    setLottoSaleAmount,
    setPayoutAmount,
    setPosAmount,
  ]);

  const state = useMemo(() => ({
    createPackLoading,
    locations,
    games,
    gamesLoading,
    hasStoreId,
    lotteryPayoutLoading,
    lottoSaleLoading,
    packsError,
    packsLoading,
    processedPacks,
    scannerConfig,
    scratcherPayoutLoading,
    selectedGame,
    selectedLocationId,
    selectedStoreId,
    storeData,
    storeName,
    totalPacks: packsData?.total || 0,
  }), [
    createPackLoading,
    locations,
    games,
    gamesLoading,
    hasStoreId,
    lotteryPayoutLoading,
    lottoSaleLoading,
    packsData?.total,
    packsError,
    packsLoading,
    processedPacks,
    scannerConfig,
    scratcherPayoutLoading,
    selectedGame,
    selectedLocationId,
    selectedStoreId,
    storeData,
    storeName,
  ]);

  const actions = useMemo(() => ({
    dismissError,
    handleCreatePack,
    handleGameChange,
    handleLottoSale,
    handleLottoSaleCancel,
    handleLottoSaleSubmit,
    handleLocationChange,
    handlePackReturn,
    handlePayoutCancel,
    handlePayoutSubmit,
    onLotteryPayout,
  }), [
    dismissError,
    handleCreatePack,
    handleGameChange,
    handleLottoSale,
    handleLottoSaleCancel,
    handleLottoSaleSubmit,
    handleLocationChange,
    handlePackReturn,
    handlePayoutCancel,
    handlePayoutSubmit,
    onLotteryPayout,
  ]);

  return {
    dialogs,
    state,
    actions,
  };
};

export default useCaseSimulatorPageLogic;
